// src/components/NewsmatcherView.jsx

import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import ArticleModal from './ArticleModal';
import { fetchNewsV2, fetchNewsArticleDetail } from '../api/fetchNews';
import { submitArticleFeedback } from '../api/feedback';
import { AuthContext } from '../contexts/AuthContext';

// Friendly messages for the loading spinner.
const friendlyMessages = [
  "Loading...",
  "Fetching fresh articles...",
  "Hang tight, almost there..."
];

function NewsmatcherView() {
  // Vertical sliding and feedback states.
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailedArticle, setDetailedArticle] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [horizontalAnimating, setHorizontalAnimating] = useState(false);
  // New state to show a large overlay label when a button is tapped.
  const [feedbackLabel, setFeedbackLabel] = useState(null);
  
  // We'll use a state for the viewport height.
  const [viewportHeight, setViewportHeight] = useState(0);
  
  // Instead of reading window immediately, initialize mobile state to false and update in useEffect.
  const [isMobile, setIsMobile] = useState(false);

  // Update viewport and mobile status in useEffect (runs after mount)
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
      setIsMobile(window.innerWidth < 768);
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Swipe direction lock state.
  const [swipeDirection, setSwipeDirection] = useState(null);

  // State for touch start.
  const [dragStart, setDragStart] = useState(null);

  // Refs for throttling and direct DOM updates.
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const animationFrameIdRef = useRef(null);
  const activeCardRef = useRef(null); // Ref to the active card element.

  // Get allowedDomains from AuthContext.
  const { allowedDomains } = useContext(AuthContext);

  const stableAllowedDomains = useMemo(() => {
    return Array.isArray(allowedDomains)
      ? allowedDomains.slice().sort().join(',')
      : '';
  }, [allowedDomains]);

  const queryKey = ['news', { country: 'us', size: 10, allowedDomains: stableAllowedDomains }];

  // useInfiniteQuery to fetch paginated news.
  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    queryKey,
    ({ pageParam = undefined }) =>
      fetchNewsV2({
        country: 'us',
        size: 10,
        next_page: pageParam,
        allowed_domains: allowedDomains,
      }),
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => lastPage.next_page || undefined,
    }
  );

  // Flatten pages.
  const articles = data ? data.pages.flatMap((page) => page.results || []) : [];

  // Rotate through friendly messages every 2 seconds.
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % friendlyMessages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Fetch next page if near the end.
  useEffect(() => {
    if (
      articles.length > 0 &&
      currentIndex >= articles.length - 2 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [currentIndex, articles, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Dismiss onboarding overlay after 5 seconds.
  useEffect(() => {
    const timer = setTimeout(() => setShowOnboarding(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Global style fix for Android Pull-to-Refresh.
  useEffect(() => {
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehavior = 'none';
  }, []);

  // --- Touch / Swipe Handlers ---

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setDragStart({ x: touch.clientX, y: touch.clientY });
    setSwipeDirection(null);
    if (activeCardRef.current) {
      activeCardRef.current.style.transition = 'none';
    }
  };

  const handleTouchMove = (e) => {
    if (!dragStart) return;
    const touch = e.touches[0];
    let dx = touch.clientX - dragStart.x;
    let dy = touch.clientY - dragStart.y;
  
    if (!swipeDirection) {
      if (Math.abs(dx) > Math.abs(dy)) {
        setSwipeDirection('horizontal');
      } else if (Math.abs(dy) > Math.abs(dx)) {
        setSwipeDirection('vertical');
      }
    } else if (swipeDirection === 'vertical') {
      dx = 0;
    } else if (swipeDirection === 'horizontal') {
      dy = 0;
    }
  
    // Update the mutable ref
    dragOffsetRef.current = { x: dx, y: dy };
  
    if (swipeDirection === 'horizontal') {
      // Update state for real-time re-rendering of the overlay.
      setDragOffset({ x: dx, y: dy });
      if (activeCardRef.current) {
        activeCardRef.current.style.transform = `translate3d(${dx}px, 0, 0) rotate(${dx * 0.1}deg)`;
        activeCardRef.current.style.willChange = 'transform';
      }
    } else {
      if (!animationFrameIdRef.current) {
        animationFrameIdRef.current = requestAnimationFrame(() => {
          setDragOffset({ ...dragOffsetRef.current });
          animationFrameIdRef.current = null;
        });
      }
    }
  };
  

  const handleTouchEnd = () => {
    if (!dragStart) return;
    const threshold = 50;

    if (swipeDirection === 'horizontal') {
      const absX = Math.abs(dragOffsetRef.current.x);
      if (absX > threshold) {
        handleHorizontalSwipe(dragOffsetRef.current.x > 0 ? 'relevant' : 'not_relevant');
      } else {
        if (activeCardRef.current) {
          activeCardRef.current.style.transition = 'transform 0.3s ease-out';
          activeCardRef.current.style.transform = '';
        }
      }
    } else if (swipeDirection === 'vertical') {
      const absY = Math.abs(dragOffsetRef.current.y);
      const verticalThreshold = viewportHeight * 0.15;
      if (absY > verticalThreshold) {
        if (dragOffsetRef.current.y < 0 && currentIndex < articles.length - 1) {
          setCurrentIndex((prev) => prev + 1);
        } else if (dragOffsetRef.current.y > 0 && currentIndex > 0) {
          setCurrentIndex((prev) => prev - 1);
        }
      }
    }
    setDragOffset({ x: 0, y: 0 });
    setDragStart(null);
    setSwipeDirection(null);
  };

  const handleWheel = (e) => {
    if (horizontalAnimating) return;
    if (e.deltaY > 0 && currentIndex < articles.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else if (e.deltaY < 0 && currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleHorizontalSwipe = (rating) => {
    setHorizontalAnimating(true);
    const label =
      rating === 'relevant'
        ? 'Relevant'
        : rating === 'not_relevant'
        ? 'Not Relevant'
        : 'Neutral';
    setFeedbackLabel(label);

    const targetX =
      rating === 'relevant'
        ? window.innerWidth
        : rating === 'not_relevant'
        ? -window.innerWidth
        : 0;

    const dragX = dragOffsetRef.current.x || 0;

    if (activeCardRef.current) {
      activeCardRef.current.style.transition = 'transform 0.3s ease-out, opacity 0s';
      activeCardRef.current.style.transform = `translate3d(${targetX}px, 0, 0) rotate(${dragX * 0.1}deg)`;
      activeCardRef.current.style.opacity = '0';
      activeCardRef.current.style.willChange = 'transform, opacity';
    }

    setTimeout(() => {
      submitFeedback(rating);
      setCurrentIndex((prev) => prev + 1);
      setHorizontalAnimating(false);
      setFeedbackLabel(null);
      if (activeCardRef.current) {
        activeCardRef.current.style.transition = '';
        activeCardRef.current.style.transform = '';
        activeCardRef.current.style.opacity = '';
        activeCardRef.current.style.willChange = '';
      }
    }, 300);
  };

  const submitFeedback = async (rating) => {
    if (!articles[currentIndex]) return;
    try {
      await submitArticleFeedback({
        article_id: articles[currentIndex].article_id,
        feedback: rating,
      });
      // console.log(`Feedback "${rating}" submitted.`);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleArticleClick = async (idx) => {
    if (!articles[idx]) return;
    try {
      const detail = await fetchNewsArticleDetail(articles[idx].link);
      setDetailedArticle(detail);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching detailed article:', error);
    }
  };

  // --- Layout Styles ---
  const containerStyle = !isMobile
    ? {
        transform: `translate3d(0, ${-currentIndex * viewportHeight + (swipeDirection === 'vertical' ? dragOffset.y : 0)}px, 0)`,
        transition: dragStart ? 'none' : 'transform 0.3s ease-out',
        willChange: 'transform',
      }
    : {};

  const getCardStyle = (index) => {
    if (index < currentIndex) {
      return { display: 'none' };
    }
    if (index === currentIndex) {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: articles.length,
      };
    }
    return {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: articles.length - (index - currentIndex),
    };
  };

  // --- Render Section ---

  if (isLoading && articles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-full">
        <div className="relative">
          <div
            className="w-12 h-12 rounded-full border-4 border-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 animate-spin"
            style={{ backgroundSize: '200% 200%' }}
          ></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-4 h-4 bg-white rounded-full shadow-lg"></div>
          </div>
        </div>
        <span className="mt-3 text-lg font-semibold text-gray-800">
          {friendlyMessages[currentMessageIndex]}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        Error loading articles
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 w-full bg-gray-50"
      style={{ overflow: 'hidden' }}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {showOnboarding && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="text-white text-center">
            <p className="text-xl mb-4">
              Swipe up/down or scroll to explore articles
            </p>
            <div className="animate-bounce">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 mx-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {!isMobile ? (
        <div className="absolute top-0 left-0 right-0" style={containerStyle}>
          {articles.map((article, index) => {
            const isActive = index === currentIndex;
            const overlayText = feedbackLabel || (dragOffset.x > 0 ? 'Relevant' : 'Not Relevant');
            const overlayColor = feedbackLabel 
              ? (feedbackLabel === 'Relevant' ? '#16a34a' : feedbackLabel === 'Not Relevant' ? '#dc2626' : '#000')
              : (dragOffset.x > 0 ? '#16a34a' : '#dc2626');
            
            
            return (
              <div
                key={article.article_id || article.link}
                className="flex items-center justify-center"
                style={{ height: 'calc(var(--vh, 1vh) * 100)' }}
              >
                <div
                  ref={isActive ? activeCardRef : null}
                  className="relative w-full max-w-lg bg-white shadow-xl rounded-xl mx-auto"
                  style={{
                    height: 'calc(var(--vh, 1vh) * 100)',
                    transition: dragStart ? 'none' : 'transform 0.3s ease-out',
                  }}
                >
                  <div className="p-8 h-full">
                    {isActive && (feedbackLabel || (dragStart && swipeDirection === 'horizontal')) && !horizontalAnimating && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span
                          className="text-5xl font-bold tracking-wider opacity-80"
                          style={{ color: overlayColor }}
                        >
                          {overlayText}
                        </span>
                      </div>
                    )}

                    {article.image_url && (
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-56 object-cover rounded-xl mb-6"
                      />
                    )}

                    <div onClick={() => handleArticleClick(index)} className="cursor-pointer select-none">
                      <h2 className="text-3xl font-light tracking-tight mb-3">{article.title}</h2>
                      {article.description && (
                        <p
                          className="text-lg text-gray-600 mb-2 line-clamp-7"
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 7,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {article.description}
                        </p>
                      )}
                      <div className="flex justify-between mt-4">
                        {article.source_name && (
                          <span className="text-sm text-gray-500 uppercase">
                            {article.source_name}
                          </span>
                        )}
                        {article.pubDate && (
                          <span className="text-sm text-gray-500">
                            {new Date(article.pubDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-around mt-6">
                      <button
                        onClick={() => handleHorizontalSwipe('not_relevant')}
                        className="flex-1 mx-2 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-md text-white font-semibold transition-colors shadow-sm"
                      >
                        Not Relevant
                      </button>
                      <button
                        onClick={() => handleHorizontalSwipe('neutral')}
                        className="flex-1 mx-2 px-4 py-3 bg-gray-600 hover:bg-gray-700 rounded-md text-white font-semibold transition-colors shadow-sm"
                      >
                        Neutral
                      </button>
                      <button
                        onClick={() => handleHorizontalSwipe('relevant')}
                        className="flex-1 mx-2 px-4 py-3 bg-green-600 hover:bg-green-700 rounded-md text-white font-semibold transition-colors shadow-sm"
                      >
                        Relevant
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="relative h-full w-full">
          {articles.map((article, index) => {
            const isActive = index === currentIndex;
            const cardStyle = getCardStyle(index);
            const overlayText = feedbackLabel || (dragOffset.x > 0 ? 'Relevant' : 'Not Relevant');
            const overlayColor = feedbackLabel 
              ? (feedbackLabel === 'Relevant' ? '#16a34a' : feedbackLabel === 'Not Relevant' ? '#dc2626' : '#000')
              : (dragOffset.x > 0 ? '#16a34a' : '#dc2626');
            
            
            return (
              <div key={article.article_id || article.link} style={cardStyle}>
                <div
                  ref={isActive ? activeCardRef : null}
                  className="relative w-full max-w-lg bg-white shadow-xl rounded-xl mx-auto h-full"
                  style={{
                    transition: dragStart ? 'none' : 'transform 0.3s ease-out',
                  }}
                >
                  <div className="p-8 h-full">
                    {isActive && (feedbackLabel || (dragStart && swipeDirection === 'horizontal')) && !horizontalAnimating && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span
                          className="text-5xl font-bold tracking-wider opacity-80"
                          style={{ color: overlayColor }}
                        >
                          {overlayText}
                        </span>
                      </div>
                    )}

                    {article.image_url && (
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-56 object-cover rounded-xl mb-6"
                      />
                    )}

                    <div onClick={() => handleArticleClick(index)} className="cursor-pointer select-none">
                      <h2 className="text-3xl font-light tracking-tight mb-3">{article.title}</h2>
                      {article.description && (
                        <p
                          className="text-lg text-gray-600 mb-2 line-clamp-7"
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 7,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {article.description}
                        </p>
                      )}
                      <div className="flex justify-between mt-4">
                        {article.source_name && (
                          <span className="text-sm text-gray-500 uppercase">
                            {article.source_name}
                          </span>
                        )}
                        {article.pubDate && (
                          <span className="text-sm text-gray-500">
                            {new Date(article.pubDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-around mt-6">
                      <button
                        onClick={() => handleHorizontalSwipe('not_relevant')}
                        className="flex-1 mx-2 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-md text-white font-semibold transition-colors shadow-sm"
                      >
                        Not Relevant
                      </button>
                      <button
                        onClick={() => handleHorizontalSwipe('neutral')}
                        className="flex-1 mx-2 px-4 py-3 bg-gray-600 hover:bg-gray-700 rounded-md text-white font-semibold transition-colors shadow-sm"
                      >
                        Neutral
                      </button>
                      <button
                        onClick={() => handleHorizontalSwipe('relevant')}
                        className="flex-1 mx-2 px-4 py-3 bg-green-600 hover:bg-green-700 rounded-md text-white font-semibold transition-colors shadow-sm"
                      >
                        Relevant
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isModalOpen && detailedArticle && (
        <ArticleModal
          article={detailedArticle}
          onClose={() => {
            setIsModalOpen(false);
            setDetailedArticle(null);
          }}
        />
      )}
    </div>
  );
}

export default NewsmatcherView;
